import { useEffect, useState } from 'react';
import { authFetch, isSignedIn } from '../lib/auth';
import { AuthSheet } from './AuthSheet';

/**
 * THU-242, the event funnel dashboard (?funnel=1). Owner only: the worker checks the
 * session's email against ADMIN_EMAIL and answers 403 to anyone else.
 *
 * Two funnels, because they answer different questions and GA4 can only draw one:
 *   ROOMS   - of the nights hosted, how many got a join attempt, a real join, and a
 *             guest watching something. A night is a host plus guests on different
 *             devices, so this can only be counted from bfn_events' room key.
 *   DEVICES - of the devices that tried to join, how many got in and what picture
 *             they got. This is the per-user funnel GA4's exploration draws too.
 * Plus the breakdowns that make a drop actionable: why joins failed, how people
 * tried, where people went.
 */

interface Report {
  days: number;
  since: string;
  gate: 'open' | 'shut';
  ga4: boolean;
  totals: Record<string, { events: number; devices: number }>;
  daily: { day: string; event: string; events: number; devices: number; rooms: number }[];
  rooms: {
    day: string; rooms_hosted: number; rooms_with_join: number; rooms_streaming: number;
    rooms_watching: number; rooms_with_fallback: number; rooms_with_web_guest: number;
  }[];
  failureReasons: Record<string, number>;
  joinMethods: Record<string, number>;
  streamKinds: Record<string, number>;
  fallbackPictures: Record<string, number>;
  locationMoves: Record<string, number>;
  labels: Record<string, string>;
}

// Event labels and order come from the worker's registry (EVENT_DEFS in track.ts), so a
// new event appears here the day the worker knows it. Anything stored under a name the
// registry no longer has still shows, by its raw name, after the known ones.
function eventOrder(rep: Report): string[] {
  const known = Object.keys(rep.labels ?? {});
  const extra = Object.keys(rep.totals).filter((e) => !known.includes(e)).sort();
  return [...known, ...extra];
}

function Bar({ label, value, of, note }: { label: string; value: number; of: number; note?: string }) {
  const pct = of > 0 ? Math.round((value / of) * 100) : 0;
  return (
    <div style={{ margin: '10px 0' }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
        <span>{label}</span>
        <span className="muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}{of > 0 && value !== of ? ` · ${pct}%` : ''}{note ? ` · ${note}` : ''}
        </span>
      </div>
      <div style={{ height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${of > 0 ? Math.max(2, (value / of) * 100) : 0}%`, height: '100%', background: 'var(--accent, #f5c451)' }} />
      </div>
    </div>
  );
}

function Tally({ title, data }: { title: string; data: Record<string, number> }) {
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]);
  return (
    <div className="card">
      <h2>{title}</h2>
      {rows.length === 0 ? <p className="muted">Nothing yet.</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}><td style={{ padding: '4px 0' }}>{k}</td><td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function FunnelDashboard() {
  const [signedIn] = useState(isSignedIn());
  const [days, setDays] = useState(14);
  const [rep, setRep] = useState<Report | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    setErr(null);
    authFetch(`/api/admin/funnel?days=${days}`)
      .then(async (r) => {
        if (!alive) return;
        if (r.status === 403) { setErr('This dashboard is for the Thursday Night admin account.'); return; }
        if (!r.ok) { setErr(`Could not load the funnel (HTTP ${r.status}).`); return; }
        setRep((await r.json()) as Report);
      })
      .catch(() => alive && setErr('Could not reach the server.'));
    return () => { alive = false; };
  }, [signedIn, days]);

  if (!signedIn) return <AuthSheet title="Sign in to see the funnel" />;
  if (err) return <div className="card"><h2>Event funnel</h2><p>{err}</p></div>;
  if (!rep) return <div className="card"><h2>Event funnel</h2><p className="muted">Loading…</p></div>;

  const sum = (k: keyof Report['rooms'][number]) => rep.rooms.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const hosted = sum('rooms_hosted');
  const t = (e: string) => rep.totals[e]?.devices ?? 0;
  const tried = t('join_attempt');

  return (
    <div>
      <div className="card">
        <h2>Event funnel</h2>
        <p className="muted">
          Last {rep.days} days (since {rep.since}). Collection is <b>{rep.gate === 'open' ? 'ON' : 'OFF'}</b>
          {rep.gate === 'shut' ? ' — waiting on the privacy-policy update' : ''}; Google Analytics forwarding is <b>{rep.ga4 ? 'ON' : 'not configured'}</b>.
        </p>
        <div className="row" style={{ gap: 8 }}>
          {[1, 7, 14, 30, 90].map((d) => (
            <button key={d} className="btn" onClick={() => setDays(d)} aria-pressed={d === days}
              style={d === days ? { outline: '2px solid var(--accent, #f5c451)' } : undefined}>{d === 1 ? 'Today' : `${d} days`}</button>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Rooms: did the night work?</h2>
        <Bar label="Nights hosted" value={hosted} of={hosted} />
        <Bar label="…a guest got in" value={sum('rooms_with_join')} of={hosted} />
        <Bar label="…the host streamed (desktop or movie)" value={sum('rooms_streaming')} of={hosted} />
        <Bar label="…someone watched the share or the movie" value={sum('rooms_watching')} of={hosted} />
        <Bar label="…someone hit the fallback picture" value={sum('rooms_with_fallback')} of={hosted} />
        <Bar label="…a web (MST3K) guest joined" value={sum('rooms_with_web_guest')} of={hosted} />
      </div>

      <div className="card">
        <h2>Devices: did each guest get in?</h2>
        <Bar label="Tried to join" value={tried} of={tried} />
        <Bar label="Got in" value={t('join_success')} of={tried} />
        <Bar label="Could not get in" value={t('join_failure')} of={tried} />
        <Bar label="Saw the desktop share" value={t('view_desktop_share')} of={tried} />
        <Bar label="Saw the movie" value={t('view_movie_share')} of={tried} />
        <Bar label="On the fallback picture" value={t('view_stream_fallback')} of={tried} />
      </div>

      <div className="card">
        <h2>All events</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={{ textAlign: 'left' }}>Event</th><th style={{ textAlign: 'right' }}>Events</th><th style={{ textAlign: 'right' }}>Devices</th></tr></thead>
          <tbody>
            {eventOrder(rep).map((e) => (
              <tr key={e}>
                <td style={{ padding: '4px 0' }}>{rep.labels?.[e] ?? e} <span className="muted">({e})</span></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{rep.totals[e]?.events ?? 0}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{rep.totals[e]?.devices ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Tally title="Why joins failed" data={rep.failureReasons} />
      <Tally title="How people tried to join" data={rep.joinMethods} />
      <Tally title="What hosts streamed" data={rep.streamKinds} />
      <Tally title="Fallback picture states" data={rep.fallbackPictures} />
      <Tally title="Location changes" data={rep.locationMoves} />
    </div>
  );
}
